import React, { useState } from 'react';
import {
  Button,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Divider,
  Card,
  CardSection,
  CardFooter,
  SmallHeader,
  CardHeader,
  Paragraph,
  ImageCircle,
  ParagraphHeader,
  TalentExperiences,
  TalentCertifications,
  TalentQualifications,
  TalentLanguages,
  CandidateBadges,
  OccupationTypeUtil,
  TalentEducation,
  Layout,
  PillsItem,
  CondensedMedium,
  Flashy,
  Pills,
  useBreakpoint,
  LayoutItem,
  Suspense,
  ToastUtil,
  CondensedLarge,
  LegalTechSkillsDisplay,
  TaxonomyUtil,
} from '@axiom/ui';
import {
  PossibleImageSizes,
  Education,
  BarredLocationsConst,
  DisplayPronouns,
  Certification,
} from '@axiom/const';
import {
  CandidateYearsOfExperienceModel,
  CandidateIndustryModel,
} from '@axiom/ui-models';
import type {
  CandidateLegalTechSkill,
  GroupedExperiences,
  LimitedCandidate,
  Taxonomy,
  User,
} from '@axiom/validation';

import { FormInquireWithAxiomModalStore } from '../../stores/form-inquire-with-axiom-modal-store';
import { CandidateUtil } from '../../utils/candidate-util';
import { BarredLocationsUtil } from '../../utils/barred-locations-util';
import {
  isAFCUser,
  isClientUser,
  isLoggedInUser,
  isLpUser,
} from '../../utils/roles-util';
import { WindowUtil } from '../../utils/window-util';
import { FavoriteButton } from '../FavoriteButton/FavoriteButton';
import { PricingUtil } from '../../utils/pricing-util';
import { CandidateBios } from '../CandidateBios/CandidateBios';

import { TalentDetailsRateLimitInfo } from './TalentDetailsRateLimitInfo';

const emptyPlaceholder = '--';

const { QUALIFICATION_AWARDS } = Certification;

export type TalentDetailsProps = {
  candidate: LimitedCandidate;
  groupedExperiences: GroupedExperiences[];
  user?: User | null;
  taxonomy: Taxonomy;
  legalTechSkills?: CandidateLegalTechSkill[] | null;
};

export const TalentDetails = ({
  candidate,
  groupedExperiences,
  user,
  taxonomy,
  legalTechSkills,
}: TalentDetailsProps) => {
  const [downloadPdf, setDownloadPdf] = useState(false);
  const { isMobile } = useBreakpoint();
  const isUserLoggedIn = !!(user && isLoggedInUser(user));
  const isAuthorized = !!(user && isAFCUser(user));
  const industries = new CandidateIndustryModel(
    candidate.industries ?? []
  ).formatIndustries();
  const lawDegreeSchools = candidate.degrees?.filter(
    item =>
      item.degree && Object.keys(Education.LAW_DEGREES).includes(item.degree)
  );
  const yearsOfExperienceRange = new CandidateYearsOfExperienceModel(
    candidate.yearsOfExperience
  ).getYearsOfExperienceRange();
  const formattedPracticeAreaFocusArea =
    TaxonomyUtil.formatPracticeAreaFocusAreaForDisplay(
      taxonomy,
      candidate.primaryPracticeArea,
      candidate.primaryFocusArea
    );
  const formattedLawFirms = CandidateUtil.getFormattedLawFirms(candidate);
  const formattedLocation = CandidateUtil.formatCandidateLocation(
    candidate,
    isAuthorized
  );
  const formattedLegalRole = OccupationTypeUtil.professionFromOccupationType(
    candidate.occupationType,
    user
  );
  const filteredBarredLocations = candidate.barredLocations?.filter(
    admission => admission.name !== BarredLocationsConst.Names.NotAdmitted
  );
  const numberOfHighlights = [
    industries.length > 0,
    formattedPracticeAreaFocusArea,
    yearsOfExperienceRange !== emptyPlaceholder,
    (lawDegreeSchools?.length ?? 0) > 0,
    formattedLocation,
    (filteredBarredLocations?.length ?? 0) > 0,
    formattedLegalRole && formattedLegalRole !== 'Lawyer',
  ].filter(Boolean).length;

  const { fullTimeDailyRate, adHocHourlyRate, pricingAvailable } =
    PricingUtil.getPricingData(candidate, user);

  const { practiceAreas } = taxonomy;
  const awardTypes = Object.values(QUALIFICATION_AWARDS);
  const calculateExistenceOfCertificationsAndQualifications = (
    candidate.certifications ?? []
  ).reduce(
    (acc, cert) => {
      if (awardTypes.includes(cert.award)) {
        acc.hasQualifications = true;
      } else {
        acc.hasCertifications = true;
      }
      return acc;
    },
    { hasCertifications: false, hasQualifications: false }
  );
  const { hasCertifications, hasQualifications } =
    calculateExistenceOfCertificationsAndQualifications;

  const handleDownloadClick = () => {
    setDownloadPdf(true);

    const toastName = 'DOWNLOAD_BIO_PDF';
    const toasts = ToastUtil.getAll();
    const alreadyOpen = toasts.some(toast => toast.name === toastName);
    if (!alreadyOpen) {
      ToastUtil.add({
        name: toastName,
        type: 'info',
        children: (
          <CondensedLarge>
            Your PDF is being created - it will download automatically when
            ready.
          </CondensedLarge>
        ),
      });
    }
  };

  return (
    <>
      <Card name="TALENT_DETAILS_CARD">
        <CardSection divider>
          <Layout
            horizontalGutter="16px"
            stackableOn="mobile"
            verticalGutter="16px"
          >
            <LayoutItem>
              <ImageCircle
                name="TALENT_DETAILS_IMAGE_CIRCLE"
                imageName={candidate.calculatedDisplayName}
                src={CandidateUtil.getProfileImageUri(
                  candidate,
                  PossibleImageSizes.W_100
                )}
              />
            </LayoutItem>
            <LayoutItem fluid>
              <Layout direction="vertical">
                <LayoutItem bottomGutter="8px">
                  <Layout position="middle" horizontalGutter="8px">
                    <LayoutItem>
                      <SmallHeader name="TALENT_DETAILS_CALCULATED_DISPLAY_NAME">
                        {candidate.calculatedDisplayName}
                      </SmallHeader>
                    </LayoutItem>
                    <LayoutItem fluid>
                      {candidate.pronouns ? (
                        <Paragraph name="TALENT_DETAILS_PRONOUNS">
                          {DisplayPronouns[candidate.pronouns]}
                        </Paragraph>
                      ) : null}
                    </LayoutItem>
                  </Layout>
                </LayoutItem>
                <LayoutItem bottomGutter="8px">
                  <CandidateBadges
                    badges={[
                      ...(candidate.availabilityStatus
                        ? [candidate.availabilityStatus]
                        : []),
                      ...candidate.badges,
                    ]}
                  />
                </LayoutItem>
                <LayoutItem fluid>
                  <Paragraph name="TALENT_DETAILS_PRACTICE_AREA_FOCUS_AREA">
                    {formattedPracticeAreaFocusArea}
                  </Paragraph>
                  <Paragraph name="TALENT_DETAILS_LOCATION">
                    {formattedLocation}
                  </Paragraph>
                </LayoutItem>
              </Layout>
            </LayoutItem>
            {!!pricingAvailable && (
              <LayoutItem>
                <Layout
                  direction="vertical"
                  position="right"
                  verticalGutter="8px"
                >
                  {isMobile ? (
                    <Flashy name="TALENT_DETAILS_LOW_RATE_FLASHY" bold>
                      Starting at {fullTimeDailyRate} for a five-day week
                    </Flashy>
                  ) : (
                    <Pills>
                      <PillsItem
                        pattern="secondary"
                        name="TALENT_DETAILS_LOW_RATE_PILL"
                      >
                        Starting at {fullTimeDailyRate} for a five-day week
                      </PillsItem>
                    </Pills>
                  )}
                  {!!adHocHourlyRate &&
                  candidate.primaryPracticeArea &&
                  practiceAreas[candidate.primaryPracticeArea]?.fullName !==
                    practiceAreas.LegalSupport?.fullName ? (
                    <CondensedMedium
                      name="TALENT_DETAILS_HIGH_RATE"
                      className={isMobile ? undefined : 'text-align-right'}
                    >
                      <Flashy color="textSubtle">
                        up to {adHocHourlyRate} for ad hoc hourly
                      </Flashy>
                    </CondensedMedium>
                  ) : null}
                </Layout>
              </LayoutItem>
            )}
          </Layout>
        </CardSection>
        <CardFooter stackableOn="mobile">
          {isUserLoggedIn && !isLpUser(user) ? (
            <FavoriteButton user={user} candidate={candidate} />
          ) : null}
          {isUserLoggedIn && !isLpUser(user) ? (
            <Button
              name="DOWNLOAD_BIO"
              variation="outline"
              onClick={() => {
                handleDownloadClick();
              }}
            >
              Download
            </Button>
          ) : null}
          <Button
            name="INQUIRE"
            onClick={async () => {
              await FormInquireWithAxiomModalStore.load(
                candidate,
                isClientUser(user) ? user : {}
              );
            }}
          >
            Request Info
          </Button>
        </CardFooter>
      </Card>
      <Gutter bottom="16px" />
      {!!candidate.candidateSummary && (
        <Gutter bottom="16px">
          <Card>
            <CardHeader>
              <SmallHeader>Summary</SmallHeader>
            </CardHeader>
            <CardSection>
              <Paragraph name="TALENT_DETAILS_SUMMARY">
                {candidate.candidateSummary}
              </Paragraph>
            </CardSection>
          </Card>
        </Gutter>
      )}
      {numberOfHighlights > 0 && (
        <Card name="TALENT_DETAILS_HIGHLIGHTS">
          <CardHeader>
            <SmallHeader>Highlights</SmallHeader>
          </CardHeader>
          <CardSection>
            <Grid stackable>
              {!!formattedPracticeAreaFocusArea && (
                <GridRow gutterBottom="16px">
                  <GridColumn width={3}>
                    <ParagraphHeader>Practice Area</ParagraphHeader>
                  </GridColumn>
                  <GridColumn width={9}>
                    <Paragraph name="TALENT_DETAILS_HIGHLIGHTS_PRACTICE_AREA">
                      {formattedPracticeAreaFocusArea}
                    </Paragraph>
                  </GridColumn>
                </GridRow>
              )}
              {industries.length > 0 && (
                <GridRow gutterBottom="16px">
                  <GridColumn width={3}>
                    <ParagraphHeader>Industries</ParagraphHeader>
                  </GridColumn>
                  <GridColumn width={9}>
                    <Paragraph name="TALENT_DETAILS_HIGHLIGHTS_INDUSTRIES">
                      <Divider>
                        {industries.map(
                          ({ industryValue, yearsOfExperience }) => (
                            <span key={industryValue}>
                              <span>
                                {
                                  taxonomy.industries[industryValue]
                                    ?.displayValue
                                }
                              </span>
                              &nbsp;
                              <span>
                                (
                                {CandidateUtil.formatYOE(
                                  `${yearsOfExperience}`
                                )}
                                )
                              </span>
                            </span>
                          )
                        )}
                      </Divider>
                    </Paragraph>
                  </GridColumn>
                </GridRow>
              )}
              {yearsOfExperienceRange !== emptyPlaceholder && (
                <GridRow gutterBottom="16px">
                  <GridColumn width={3}>
                    <ParagraphHeader>Years of Experience</ParagraphHeader>
                  </GridColumn>
                  <GridColumn width={9}>
                    <Paragraph name="TALENT_DETAILS_HIGHLIGHTS_YEARS_OF_EXPERIENCE">
                      <span>{yearsOfExperienceRange}</span>
                    </Paragraph>
                  </GridColumn>
                </GridRow>
              )}
              {!!pricingAvailable && (
                <GridRow gutterBottom="16px">
                  <GridColumn width={3}>
                    <ParagraphHeader>Rate</ParagraphHeader>
                  </GridColumn>
                  <GridColumn width={9}>
                    <Paragraph name="TALENT_DETAILS_HIGHLIGHTS_RATE">
                      Starting at {fullTimeDailyRate} for a five-day week
                      {!!adHocHourlyRate &&
                        `; Up to ${adHocHourlyRate} for ad hoc hourly`}
                    </Paragraph>
                  </GridColumn>
                </GridRow>
              )}
              {(formattedLawFirms?.length ?? 0) > 0 && (
                <GridRow gutterBottom="16px">
                  <GridColumn width={3}>
                    <ParagraphHeader>Law Firms</ParagraphHeader>
                  </GridColumn>
                  <GridColumn width={9}>
                    <Paragraph name="TALENT_DETAILS_HIGHLIGHTS_LAW_FIRMS">
                      <Divider>
                        {formattedLawFirms?.map(({ client }) => (
                          <span key={client}>{client}</span>
                        ))}
                      </Divider>
                    </Paragraph>
                  </GridColumn>
                </GridRow>
              )}
              {(lawDegreeSchools?.length ?? 0) > 0 && (
                <GridRow gutterBottom="16px">
                  <GridColumn width={3}>
                    <ParagraphHeader>Law Schools</ParagraphHeader>
                  </GridColumn>
                  <GridColumn width={9}>
                    <Paragraph name="TALENT_DETAILS_HIGHLIGHTS_LAW_DEGREE_SCHOOLS">
                      <Divider>
                        {lawDegreeSchools?.map(({ institution }) => (
                          <span key={institution}>{institution}</span>
                        ))}
                      </Divider>
                    </Paragraph>
                  </GridColumn>
                </GridRow>
              )}
              {!!formattedLocation && (
                <GridRow gutterBottom="16px">
                  <GridColumn width={3}>
                    <ParagraphHeader>Location</ParagraphHeader>
                  </GridColumn>
                  <GridColumn width={9}>
                    <Paragraph name="TALENT_DETAILS_HIGHLIGHTS_LOCATION">
                      {formattedLocation}
                    </Paragraph>
                  </GridColumn>
                </GridRow>
              )}
              {(filteredBarredLocations?.length ?? 0) > 0 && (
                <GridRow gutterBottom="16px">
                  <GridColumn width={3}>
                    <ParagraphHeader>Admissions</ParagraphHeader>
                  </GridColumn>
                  <GridColumn width={9}>
                    <Paragraph name="TALENT_DETAILS_HIGHLIGHTS_BARRED_LOCATIONS">
                      <Divider>
                        {candidate.barredLocations?.map(({ name }) => {
                          return (
                            <span key={name}>
                              {BarredLocationsUtil.replaceCanadian(name)}
                            </span>
                          );
                        })}
                      </Divider>
                    </Paragraph>
                  </GridColumn>
                </GridRow>
              )}
              {!!formattedLegalRole && formattedLegalRole !== 'Lawyer' && (
                <GridRow>
                  <GridColumn width={3}>
                    <ParagraphHeader>Legal Role</ParagraphHeader>
                  </GridColumn>
                  <GridColumn width={9}>
                    <Paragraph name="TALENT_DETAILS_HIGHLIGHTS_LEGAL_ROLE">
                      {formattedLegalRole}
                    </Paragraph>
                  </GridColumn>
                </GridRow>
              )}
            </Grid>
          </CardSection>
        </Card>
      )}
      <Gutter bottom="16px" />
      {!isUserLoggedIn && (
        <Gutter bottom="16px">
          <Card name="TALENT_DETAILS_SIGN_IN">
            <CardHeader>
              <SmallHeader>Experiences</SmallHeader>
            </CardHeader>
            <CardSection divider>
              <Paragraph>
                Sign in to see {candidate.calculatedDisplayName}'s experience.
              </Paragraph>
            </CardSection>
            <CardFooter>
              <Button
                name="SIGN_IN"
                className="gtm-talent-details-sign-in"
                onClick={() => {
                  WindowUtil.toLogin();
                }}
              >
                Sign In
              </Button>
            </CardFooter>
          </Card>
        </Gutter>
      )}
      {!!isAuthorized && !!candidate.restricted && (
        <Gutter bottom="16px">
          <TalentDetailsRateLimitInfo />
        </Gutter>
      )}
      {groupedExperiences.length > 0 && (
        <Gutter bottom="16px">
          <Card name="TALENT_DETAILS_EXPERIENCES">
            <CardHeader>
              <SmallHeader>Experience</SmallHeader>
            </CardHeader>
            <TalentExperiences groupedExperiences={groupedExperiences} />
          </Card>
        </Gutter>
      )}
      {!!legalTechSkills && legalTechSkills.length > 0 && (
        <Gutter bottom="16px">
          <Card name="TALENT_DETAILS_LEGAL_TECHNICAL_SKILLS">
            <CardHeader>
              <SmallHeader>Legal Tech Skills</SmallHeader>
            </CardHeader>
            <CardSection>
              <LegalTechSkillsDisplay
                legalTechSkills={legalTechSkills}
                taxonomy={taxonomy}
              />
            </CardSection>
          </Card>
        </Gutter>
      )}
      {(candidate.degrees?.length ?? 0) > 0 && (
        <Gutter bottom="16px">
          <Card name="TALENT_DETAILS_EDUCATION">
            <CardHeader>
              <SmallHeader>Education</SmallHeader>
            </CardHeader>
            <CardSection>
              <TalentEducation candidate={candidate} />
            </CardSection>
          </Card>
        </Gutter>
      )}
      {(candidate.certifications?.length ?? 0) > 0 && (
        <Gutter bottom="16px">
          <Card name="TALENT_DETAILS_ACCOMPLISHMENTS">
            <CardHeader>
              <SmallHeader>Accomplishments</SmallHeader>
            </CardHeader>
            {hasCertifications ? (
              <CardSection
                divider
                name="TALENT_DETAILS_CERTIFICATIONS_SUBSECTION"
              >
                <TalentCertifications candidate={candidate} />
              </CardSection>
            ) : null}
            {hasQualifications ? (
              <CardSection name="TALENT_DETAILS_QUALIFICATIONS_SUBSECTION">
                <TalentQualifications candidate={candidate} />
              </CardSection>
            ) : null}
          </Card>
        </Gutter>
      )}
      {(candidate.languages?.length ?? 0) > 0 && (
        <Gutter bottom="16px">
          <Card name="TALENT_DETAILS_LANGUAGES">
            <CardHeader>
              <SmallHeader>Languages</SmallHeader>
            </CardHeader>
            <CardSection>
              <TalentLanguages candidate={candidate} />
            </CardSection>
          </Card>
        </Gutter>
      )}
      {downloadPdf ? (
        <Suspense fallback={null}>
          <div className="k-pdf-export" data-test="bio-wrapper">
            <CandidateBios
              onSave={() => {
                setDownloadPdf(!downloadPdf);
              }}
              isDownloading
              candidateId={candidate.id}
            />
          </div>
        </Suspense>
      ) : null}
    </>
  );
};
